import { TabContext } from "@mui/lab";
import { Box, Stack, Tab } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../../context/langContext";
import { dateFormatter } from "../../../../utils/date-formatter";
import CustomTable from "../../../common/tables/Table";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import {
  TicketListColumnFilterable,
  TicketListColumnSortable,
  TicketListRequest,
} from "../../../../services/Endpoints/tickets/TicketListEndpoints";
import ViewAction from "../../../common/view-action/ViewAction";
import InfoBadge, { OriginEnum } from "../../../common/info-badge/InfoBadge";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { routerPaths } from "../../../../constants/Paths";
import { useNotificationContext } from "../../../../context/notificationContext";
import errorResolver from "../../../../utils/error-resolver";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../../services/Schemas/ListQueryString";
import { TicketStatusEnum } from "../../../../services/Schemas/Ticket";
import MuiTabPanel from "../../../common/tab-panel/TabPanel";
import { useUrl } from "../../../../hooks/urlEncoding";
import UserLink from "../../common/UserLink";
import { useCanAccess } from "../../../../hooks/canAccess";
import { AccessEnums } from "../../../../constants/accessEnum";
import ViewActionLink from "../../../common/view-action/ViewActionLink";
import { ITableSchema } from "../../../../utils/types";
import useTableSchema from "../../../../hooks/useTableSchema";
import NumberFilter from "../../../common/tables/filters/NumberFilter";
import TextFilter from "../../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../../common/tables/filters/SelectionFilter";
import useUrlSort from "../../../../hooks/useUrlSort";

interface ITicket {
  id: number;
  caption: string;
  created_at: string;
  department: string;
  status: string | ReactNode;
  ticket_number: string;
  operations: ReactNode;
}

const _department = {
  technical: "فنی",
  sale: "فروش",
  admin: "ادمین",
};

const statusOptions = {
  [TicketStatusEnum.PENDING]: "در انتظار بررسی",
  [TicketStatusEnum.IN_PROGRESS]: "در حال بررسی",
  [TicketStatusEnum.ANSWERED]: "پاسخ داده شده",
  [TicketStatusEnum.CLOSED]: "بسته شده",
};

export interface ISupportTabsComponent {
  ticketReload: boolean;
  setTicketReload: (payload: boolean) => void;
}

export default function TicketTabsComponent({
  ticketReload,
  setTicketReload,
}: Readonly<ISupportTabsComponent>) {
  const { parseQueryString , setParamWithSearchParams} = useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value, setValue] = useState("1");
  const { lang } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [data, setData] = useState<ITicket[] | []>([]);
  const [total, setTotal] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const { can } = useCanAccess();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const location = useLocation();

  const getData = async () => {
    try {
      setWaiting(true);
      const ticketListRequest = new TicketListRequest(protectedInstance);
      const resp = await ticketListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const tickets = resp.data.tickets.map(
        ({
          caption,
          created_at,
          department,
          status,
          ticket_number,
          id,
          creator,
        }) => ({
          id,
          caption:
            caption.length > 20 ? caption.substring(0, 20) + "..." : caption,
          creator: (
            <UserLink
              disabled={!can(AccessEnums.AdminUsersList)}
              user={creator}
            />
          ),
          created_at,
          department: _department[department as "technical"],
          status: (
            <InfoBadge
              origin={OriginEnum.TICKET}
              status={status as "pending"}
            />
          ),
          ticket_number,
          operations: (
            <Stack justifyContent="center" alignItems="center">
              {" "}
              <ViewActionLink
                disabled={!can(AccessEnums.AdminTicketsDetails)}
                link={`/${routerPaths.SUPPORT}/${id}`}
              />
            </Stack>
          ),
        })
      );
      setData(tickets);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [ticketReload, page, rowsPerPage, value, location.search]);

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true
    },
    {
      key: "ticket_number",
      label: "کد پیگیری",
      minWidth: 170,
      FilterComponent: NumberFilter,
      align: "center",
    },
    {
      key: "caption",
      label: "عنوان تیکت",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "creator",
      label: "ایجاد کننده",
      minWidth: 170,
      align: "center",
    },
    {
      key: "department",
      label: "واحد پیگیری",
      minWidth: 170,
      align: "center",
      FilterComponent: SelectionFilter,
      options:_department
    },
    {
      key: "created_at",
      label: "ایجاد شده در",
      minWidth: 100,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "status",
      label: "وضعیت",
      minWidth: 170,
      align: "center",
      FilterComponent: SelectionFilter,
      options:statusOptions
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema , columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setTicketReload(!ticketReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={data}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
